/**
 * ticketflow/DisplaysApproval — onglet « 📺 Écrans » d'un site (éditeur only).
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * Rendu comme ONGLET à l'intérieur de l'admin d'un site relayé (après « Gérer »),
 * PAS comme surcouche de la liste des sites. LISTE les affichages (via la liste
 * SIGNÉE relayée `GET /api/sites/:id/displays`) et permet d'ACTIVER / suspendre
 * chacun — utile pour les écrans supplémentaires (facturés). Chaque ordre est
 * SIGNÉ Ed25519 par la console : le client ne peut pas approuver ses propres écrans.
 *
 * Ces appels sont SAME-ORIGIN (cookie de session manager de la console centrale),
 * INDÉPENDANTS de l'`api` relayée du site : l'approbation est une prérogative de
 * l'ÉDITEUR, pas du serveur de site. Repli en saisie manuelle si le serveur de
 * site n'est pas encore à jour (route `/list` absente).
 */
import React, { useCallback, useEffect, useState } from 'react';

const jget = (url) => fetch(url, { credentials: 'same-origin' });
const jpost = (url, body) =>
  fetch(url, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });

export default function DisplaysApproval({ siteId, siteName }) {
  const [list, setList] = useState(null);
  const [err, setErr] = useState('');
  const [manual, setManual] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setErr('');
    try {
      const r = await jget(`/api/sites/${siteId}/displays`);
      if (!r.ok) { setErr(`Liste indisponible (${r.status}) — serveur de site à jour ? Utilisez la saisie manuelle.`); setList([]); return; }
      const d = await r.json();
      setList(Array.isArray(d.data) ? d.data : []);
    } catch (e) { setErr(String(e?.message || e)); setList([]); }
  }, [siteId]);
  useEffect(() => { load(); }, [load]);

  const approve = async (displayId, enabled) => {
    if (!displayId) return;
    setBusy(displayId);
    try {
      const r = await jpost(`/api/sites/${siteId}/approve-display`, { displayId, enabled });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setErr(d.error || `Échec (${r.status})`); }
      await load();
    } catch (e) { setErr(String(e?.message || e)); }
    finally { setBusy(''); }
  };

  // Test audio ciblé (bip / voix) joué SUR l'écran distant — éprouve la chaîne son sans
  // se déplacer. L'ordre est signé côté console centrale, comme l'approbation.
  const [testing, setTesting] = useState('');
  const test = async (displayId, kind) => {
    if (!displayId) return;
    setTesting(`${displayId}:${kind}`);
    setErr('');
    try {
      const r = await jpost(`/api/sites/${siteId}/test-display`, { displayId, kind });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setErr(d.error || `Test refusé (${r.status})`); }
    } catch (e) { setErr(String(e?.message || e)); }
    finally { setTesting(''); }
  };

  // Bascule master/slave (son on/off) de l'écran, ordre signé par la console centrale.
  const toggleSound = async (displayId, sound) => {
    if (!displayId) return;
    setBusy(displayId);
    setErr('');
    try {
      const r = await jpost(`/api/sites/${siteId}/sound-display`, { displayId, sound });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setErr(d.error || `Échec (${r.status})`); }
      await load();
    } catch (e) { setErr(String(e?.message || e)); }
    finally { setBusy(''); }
  };

  return (
    <div className="card">
      <h2>
        Écrans — {siteName}{' '}
        <button type="button" className="ghost" onClick={load}>↻</button>
      </h2>
      <p className="muted">
        Activez les écrans (les <b>supplémentaires</b> sont facturés). Chaque ordre est <b>signé</b> par la
        console — le client ne peut pas s'auto-approuver.
      </p>
      {err && <div className="status">{err}</div>}

      {list === null ? <p className="muted">…</p> : list.length === 0 ? (
        <>
          <p className="muted">Aucun écran listé. Saisissez l'identifiant affiché sur l'écran « En attente » :</p>
          <div className="server-add">
            <input placeholder="displayId (ex. disp-a1b2c3d4)" value={manual} onChange={(e) => setManual(e.target.value)} />
            <button type="button" disabled={!manual.trim() || busy} onClick={() => approve(manual.trim(), true)}>✅ Approuver</button>
            <button type="button" className="ghost" disabled={!manual.trim() || busy} onClick={() => approve(manual.trim(), false)}>⛔ Suspendre</button>
          </div>
        </>
      ) : (
        <table className="perm-matrix">
          <thead><tr><th>Écran</th><th>État</th><th>Son</th><th>Voix</th><th>Test audio</th><th>Approbation</th><th /></tr></thead>
          <tbody>
            {list.map((d) => (
              <tr key={d.displayId}>
                <td><b>{d.name}</b> <span className="muted">({d.displayId})</span></td>
                <td>{d.online ? <span className="badge">en ligne</span> : <span className="muted">hors ligne</span>}</td>
                <td>
                  {/* master = son ON (annonce), slave = muet. Bascule signée par la console. */}
                  <span className={d.sound ? 'badge' : 'muted'}>{d.sound ? '🔊 master' : '🔇 slave'}</span>{' '}
                  <button type="button" className="ghost" disabled={busy === d.displayId} onClick={() => toggleSound(d.displayId, !d.sound)}>
                    {d.sound ? 'Rendre muet' : 'Activer'}
                  </button>
                </td>
                <td title={d.voices?.langs?.length ? `Langues énumérées : ${d.voices.langs.join(', ')}` : "Aucune voix énumérée — l'écran peut tout de même parler avec la voix par défaut du moteur (fréquent sur Android). Testez avec 🗣 Voix."}>
                  {/* `0` n'est PAS un défaut : énumération et synthèse sont découplées sur
                      Android. Signal de santé fiable = le bouton 🗣, pas ce compteur. */}
                  {d.voices
                    ? <span className="muted">🎙 {d.voices.count}{d.voices.langs?.length ? ` (${d.voices.langs.join(', ')})` : ''}</span>
                    : <span className="muted">—</span>}
                </td>
                <td>
                  {/* Joue SUR l'écran distant, quel que soit son réglage son. */}
                  <button type="button" className="ghost" disabled={!d.online || testing === `${d.displayId}:beep`} onClick={() => test(d.displayId, 'beep')}>
                    {testing === `${d.displayId}:beep` ? '…' : '🔔 Bip'}
                  </button>{' '}
                  <button type="button" className="ghost" disabled={!d.online || testing === `${d.displayId}:voice`} onClick={() => test(d.displayId, 'voice')}>
                    {testing === `${d.displayId}:voice` ? '…' : '🗣 Voix'}
                  </button>
                </td>
                <td>{d.enabled ? <span className="badge">✅ approuvé</span> : <span className="muted">⛔ en attente</span>}</td>
                <td>
                  {d.enabled ? (
                    <button type="button" className="ghost" disabled={busy === d.displayId} onClick={() => approve(d.displayId, false)}>Suspendre</button>
                  ) : (
                    <button type="button" disabled={busy === d.displayId} onClick={() => approve(d.displayId, true)}>Approuver (facturer)</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
